// Vendors
import { useState, useRef, useEffect } from 'react';

// Components
import { Icon } from 'texkit/dist/components/Icon/Icon';

// Styles
import './accessibility-select.scss';

// AccessibilitySelect turns the dropdown options to html buttons so accessibility tools (e.g. Silktide) and other screen readers can discover and read out the text content to the user.
const AccessibilitySelect = ({
  name,
  options = [],
  label,
  formikProps,
  emptyOptionLabel,
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const controlRef = useRef(null);
  const ignoreCloseRef = useRef(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    if (open && focusedIndex !== null && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].focus();
    }
  }, [open, focusedIndex]);

  useEffect(() => {
    function onDocClick(e) {
      setTimeout(() => {
        if (ignoreCloseRef.current) {
          ignoreCloseRef.current = false;
          return;
        }
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      }, 0);
    }

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fieldValue =
    formikProps && formikProps.getFieldValue
      ? formikProps.getFieldValue(name)
      : formikProps && formikProps.values
      ? formikProps.values[name]
      : null;

  const selected =
    fieldValue && typeof fieldValue === "object" ? fieldValue : null;
  const displayLabel = selected
    ? selected.question || selected.label || String(selected)
    : emptyOptionLabel || "Select...";

  const handleSelect = (option, e, submit = false) => {
    if (formikProps && formikProps.setFieldValue) {
      formikProps.setFieldValue(name, option);
    }
    if (formikProps && formikProps.setFieldTouched) {
      formikProps.setFieldTouched(name, true);
    }
    if (submit && formikProps && typeof formikProps.submitForm === "function") {
      formikProps.submitForm();
    }
    setOpen(false);
    setTimeout(() => {
      if (
        controlRef.current &&
        typeof controlRef.current.focus === "function"
      ) {
        controlRef.current.focus();
      }
    }, 0);
  };

  return (
    <div
      className={`accessibility-select input-select input-wrapper hasLabel input-label-position-above ${
        disabled ? 'accessibility-select--disabled' : ''
      }`}
      ref={ref}
    >
      <div className="input-label">{label}</div>
      <div className="input-wrapper-field">
        <button
          type="button"
          ref={controlRef}
          className="input input-select__control"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          onFocus={() => {
            ignoreCloseRef.current = true;
            setOpen(true);
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            ignoreCloseRef.current = true;
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              ignoreCloseRef.current = true;
              setOpen(true);
              setFocusedIndex(0);
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              if (!open) {
                ignoreCloseRef.current = true;
                setOpen(true);
                setFocusedIndex(0);
              } else {
                setFocusedIndex((prev) => {
                  const next =
                    prev === null ? 0 : Math.min(prev + 1, options.length - 1);
                  return next;
                });
              }
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();
              if (!open) {
                ignoreCloseRef.current = true;
                setOpen(true);
                setFocusedIndex(options.length - 1);
              } else {
                setFocusedIndex((prev) => {
                  const next =
                    prev === null ? options.length - 1 : Math.max(prev - 1, 0);
                  return next;
                });
              }
            }
          }}
        >
          {displayLabel}
          <span
            className="input-icon input-icon-select-arrow"
            aria-hidden="true"
          >
            <Icon name="chevron-down" />
          </span>
        </button>
      </div>

      {open && (
        <div className="input-select__menu input-select__menu--desktop">
          <div className="input-select__menu-list">
            {options.map((opt, index) => {
              const optLabel =
                opt && (opt.question || opt.label)
                  ? opt.question || opt.label
                  : String(opt);
              const key = (opt && (opt.quid || opt.value)) || optLabel;
              return (() => {
                const isSelected =
                  selected &&
                  ((selected.quid && opt.quid && selected.quid === opt.quid) ||
                    (selected.value &&
                      opt.value &&
                      selected.value === opt.value));
                return (
                  <div
                    key={key}
                    className={`input-select__option ${
                      isSelected ? "input-select__option--is-selected" : ""
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <button
                      type="button"
                      ref={(el) => (optionRefs.current[index] = el)}
                      className="input-select__option-button"
                      disabled={disabled}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(opt, e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setFocusedIndex((idx) =>
                            Math.min(
                              (idx === null ? index : idx) + 1,
                              options.length - 1
                            )
                          );
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setFocusedIndex((idx) =>
                            Math.max((idx === null ? index : idx) - 1, 0)
                          );
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSelect(opt, e, true);
                        }
                        if (e.key === " ") {
                          e.preventDefault();
                          handleSelect(opt, e, false);
                        }
                      }}
                    >
                      {optLabel}
                    </button>
                  </div>
                );
              })();
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilitySelect;